import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertCircle, Link2, Plus, TestTube, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Integration {
  id: string;
  name: string;
  type: string;
  status: 'connected' | 'disconnected' | 'error';
  config: Record<string, any>;
  lastTested?: Date;
  testResult?: 'success' | 'error';
}

const AVAILABLE_INTEGRATIONS = [
  {
    id: 'webhook',
    name: 'Webhook',
    description: 'Receive and send webhooks',
    icon: '🔗',
    fields: ['URL', 'Headers', 'Auth Type'],
  },
  {
    id: 'rest-api',
    name: 'REST API',
    description: 'Connect to REST endpoints',
    icon: '📡',
    fields: ['Base URL', 'API Key', 'Headers'],
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Send emails via SMTP',
    icon: '📧',
    fields: ['SMTP Host', 'Port', 'Username', 'Password'],
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Post messages to Slack',
    icon: '💬',
    fields: ['Webhook URL', 'Channel'],
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Trigger workflows on GitHub',
    icon: '🐙',
    fields: ['Token', 'Repository', 'Branch'],
  },
  {
    id: 'database',
    name: 'Database',
    description: 'Query and modify databases',
    icon: '🗄️',
    fields: ['Host', 'Port', 'Username', 'Password', 'Database'],
  },
];

export function WorkflowIntegration() {
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: '1',
      name: 'Main Webhook',
      type: 'webhook',
      status: 'connected',
      config: { url: 'https://example.com/webhook' },
      lastTested: new Date(Date.now() - 3600000),
      testResult: 'success',
    },
  ]);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedIntegrationType, setSelectedIntegrationType] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [testingId, setTestingId] = useState<string | null>(null);

  const handleAddIntegration = () => {
    if (!selectedIntegrationType) {
      toast.error('Select an integration type');
      return;
    }

    const template = AVAILABLE_INTEGRATIONS.find((i) => i.id === selectedIntegrationType);
    if (!template) return;

    const newIntegration: Integration = {
      id: Math.random().toString(36).substr(2, 9),
      name: `${template.name} - ${new Date().toLocaleTimeString()}`,
      type: selectedIntegrationType,
      status: 'disconnected',
      config: formData,
    };

    setIntegrations([...integrations, newIntegration]);
    setShowAddDialog(false);
    setSelectedIntegrationType(null);
    setFormData({});
    toast.success(`Added ${template.name} integration`);
  };

  const handleTestIntegration = (id: string) => {
    setTestingId(id);
    setTimeout(() => {
      const updated = integrations.map((i) =>
        i.id === id
          ? {
              ...i,
              status: 'connected' as const,
              lastTested: new Date(),
              testResult: 'success' as const,
            }
          : i
      );
      setIntegrations(updated);
      setTestingId(null);
      toast.success('Integration test successful');
    }, 2000);
  };

  const handleDeleteIntegration = (id: string) => {
    setIntegrations(integrations.filter((i) => i.id !== id));
    toast.success('Integration removed');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'disconnected':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      case 'error':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      default:
        return 'bg-muted';
    }
  };

  const currentTemplate = selectedIntegrationType
    ? AVAILABLE_INTEGRATIONS.find((i) => i.id === selectedIntegrationType)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground mb-2">Integrations</h2>
          <p className="text-muted-foreground">Connect external services to your workflows</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Integration
        </Button>
      </div>

      <Tabs defaultValue="connected" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="connected">
            Connected ({integrations.filter((i) => i.status === 'connected').length})
          </TabsTrigger>
          <TabsTrigger value="available">
            Available ({AVAILABLE_INTEGRATIONS.length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All ({integrations.length})
          </TabsTrigger>
        </TabsList>

        {/* Connected Tab */}
        <TabsContent value="connected" className="space-y-4">
          {integrations.filter((i) => i.status === 'connected').length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <p className="text-sm mb-4">No integrations connected yet.</p>
              <Button onClick={() => setShowAddDialog(true)}>Add Integration</Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {integrations
                .filter((i) => i.status === 'connected')
                .map((integration) => (
                  <Card key={integration.id} className="p-4 border-green-500/20 bg-green-500/5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-foreground">{integration.name}</h3>
                          <Badge className={getStatusColor(integration.status)}>
                            {integration.status}
                          </Badge>
                          {integration.testResult === 'success' && (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Type: {integration.type}
                        </p>
                        {integration.lastTested && (
                          <p className="text-xs text-muted-foreground">
                            Last tested: {integration.lastTested.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestIntegration(integration.id)}
                          disabled={testingId === integration.id}
                        >
                          <TestTube className="w-4 h-4 mr-2" />
                          {testingId === integration.id ? 'Testing...' : 'Test'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteIntegration(integration.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>

        {/* Available Tab */}
        <TabsContent value="available" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {AVAILABLE_INTEGRATIONS.map((integration) => {
              const isAdded = integrations.some((i) => i.type === integration.id);
              return (
                <Card
                  key={integration.id}
                  className="p-4 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => {
                    setSelectedIntegrationType(integration.id);
                    setShowAddDialog(true);
                  }}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="text-3xl">{integration.icon}</div>
                      {isAdded && (
                        <Badge className="bg-green-500/10 text-green-700 border-green-500/20">
                          Added
                        </Badge>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{integration.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {integration.description}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {integration.fields.map((field) => (
                        <Badge key={field} variant="outline" className="text-xs">
                          {field}
                        </Badge>
                      ))}
                    </div>
                    <Button className="w-full" variant="outline" size="sm">
                      <Link2 className="w-4 h-4 mr-2" />
                      {isAdded ? 'Add Another' : 'Add Integration'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* All Tab */}
        <TabsContent value="all" className="space-y-4">
          {integrations.length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <p className="text-sm mb-4">No integrations yet.</p>
              <Button onClick={() => setShowAddDialog(true)}>Add Integration</Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {integrations.map((integration) => (
                <Card key={integration.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground">{integration.name}</h3>
                        <Badge className={getStatusColor(integration.status)}>
                          {integration.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Type: {integration.type}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestIntegration(integration.id)}
                        disabled={testingId === integration.id}
                      >
                        <TestTube className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteIntegration(integration.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Integration Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Integration</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {!selectedIntegrationType ? (
              <div className="grid md:grid-cols-2 gap-3">
                {AVAILABLE_INTEGRATIONS.map((integration) => (
                  <button
                    key={integration.id}
                    onClick={() => setSelectedIntegrationType(integration.id)}
                    className="p-4 rounded border border-border hover:border-primary hover:bg-muted/50 transition-all text-left"
                  >
                    <div className="text-2xl mb-2">{integration.icon}</div>
                    <p className="font-semibold text-foreground text-sm">{integration.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {integration.description}
                    </p>
                  </button>
                ))}
              </div>
            ) : currentTemplate ? (
              <div className="space-y-4">
                <div className="p-4 rounded border border-border bg-muted/50">
                  <p className="text-sm font-semibold text-foreground">
                    {currentTemplate.icon} {currentTemplate.name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {currentTemplate.description}
                  </p>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {currentTemplate.fields.map((field) => (
                    <div key={field} className="space-y-2">
                      <Label htmlFor={field} className="text-sm font-medium">
                        {field}
                      </Label>
                      <Input
                        id={field}
                        placeholder={`Enter ${field.toLowerCase()}`}
                        value={formData[field] || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, [field]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>

                <Card className="p-4 bg-blue-500/5 border-blue-500/20">
                  <div className="flex gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Your credentials are encrypted and stored securely. Never share your API
                      keys or passwords.
                    </p>
                  </div>
                </Card>
              </div>
            ) : null}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setSelectedIntegrationType(null);
                setFormData({});
              }}
            >
              Cancel
            </Button>
            {selectedIntegrationType && (
              <Button
                variant="outline"
                onClick={() => setSelectedIntegrationType(null)}
              >
                Back
              </Button>
            )}
            {selectedIntegrationType && currentTemplate && (
              <Button onClick={handleAddIntegration}>Add Integration</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Integration Tips */}
      <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-blue-500/5 border-blue-500/20">
        <h3 className="font-semibold text-foreground mb-3">Integration Tips</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Test each integration after setup to ensure it's working correctly</li>
          <li>• Keep your credentials and API keys secure</li>
          <li>• Use environment variables for sensitive configuration</li>
          <li>• Monitor integration logs for troubleshooting</li>
          <li>• Disconnect integrations you're no longer using</li>
        </ul>
      </Card>
    </div>
  );
}
